import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast, confirmDialog } from "@/lib/ui";
import {
  Settings,
  Mail,
  CreditCard,
  UserCog,
  Save,
  Plus,
  Trash2,
  Send,
  CheckCircle2,
  AlertCircle,
  ArrowLeft,
  Eye,
  EyeOff,
  Type,
} from "lucide-react";

// SMTP provider presets — auto-fills host/port/TLS. User provides API key as password.
const PROVIDER_PRESETS = {
  resend: {
    label: "Resend",
    hint: "smtp.resend.com · SSL · brugernavn: resend",
    host: "smtp.resend.com",
    port: 465,
    use_tls: false,
    use_ssl: true,
    username: "resend",
  },
  sendgrid: {
    label: "SendGrid",
    hint: "smtp.sendgrid.net · TLS · brugernavn: apikey",
    host: "smtp.sendgrid.net",
    port: 587,
    use_tls: true,
    use_ssl: false,
    username: "apikey",
  },
  gmail: {
    label: "Gmail",
    hint: "smtp.gmail.com · TLS · app-password påkrævet",
    host: "smtp.gmail.com",
    port: 587,
    use_tls: true,
    use_ssl: false,
    username: "",
  },
  mailtrap: {
    label: "Mailtrap (test)",
    hint: "sandbox.smtp.mailtrap.io · dev-inbox",
    host: "sandbox.smtp.mailtrap.io",
    port: 587,
    use_tls: true,
    use_ssl: false,
    username: "",
  },
};



export default function AdminSettings() {
  const [tab, setTab] = useState("site");
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [plans, setPlans] = useState([]);
  const [err, setErr] = useState("");
  const [toast, setToast] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [s, p] = await Promise.all([
        api.get("/admin/settings"),
        api.get("/admin/plans"),
      ]);
      setSettings(s.data);
      setPlans(p.data);
    } catch (e) {
      setErr(formatApiError(e));
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3500);
  };

  const saveSection = async (section, payload) => {
    try {
      const { data } = await api.put(`/admin/settings/${section}`, payload);
      setSettings(data);
      showToast("Gemt ✓");
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  if (loading) return <div className="max-w-7xl mx-auto px-4 py-16 text-brown-500">Indlæser indstillinger…</div>;
  if (!settings) return <div className="max-w-7xl mx-auto px-4 py-16 text-destructive">{err}</div>;

  const tabs = [
    { key: "site", label: "Site & Branding", icon: <Settings className="h-4 w-4" /> },
    { key: "email", label: "Email", icon: <Mail className="h-4 w-4" /> },
    { key: "payment", label: "Betaling & Planer", icon: <CreditCard className="h-4 w-4" /> },
    { key: "profile", label: "Profil & Moderation", icon: <UserCog className="h-4 w-4" /> },
    { key: "cms", label: "Forside-indhold", icon: <Type className="h-4 w-4" /> },
  ];

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-7" data-testid="admin-settings-page">
      <div className="flex items-center gap-3">
        <Link to="/admin" className="btn-ghost text-sm" data-testid="back-to-admin">
          <ArrowLeft className="h-4 w-4" /> Admin
        </Link>
      </div>
      <div className="mt-3 eyebrow">Kontrolpanel</div>
      <h1 className="mt-2 font-serif text-4xl text-olive-700">Indstillinger</h1>
      <p className="mt-2 text-brown-600 text-sm max-w-2xl">
        Alt styres herfra. Email, betaling, profil-specifikationer og branding — uden kode.
      </p>

      {err && <div className="mt-4 text-destructive text-sm">{err}</div>}
      {toast && (
        <div className="fixed bottom-6 right-6 z-50 bg-olive-500 text-white px-5 py-3 rounded-full shadow-medium flex items-center gap-2 animate-fade-up" data-testid="settings-toast">
          <CheckCircle2 className="h-4 w-4" /> {toast}
        </div>
      )}

      <div className="mt-8 inline-flex flex-wrap gap-1 rounded-full border border-sand-300 p-1 bg-white max-w-full">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-full text-sm inline-flex items-center gap-2 transition ${tab === t.key ? "bg-olive-500 text-white" : "text-brown-600 hover:bg-sand-100"}`}
            data-testid={`settings-tab-${t.key}`}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      <div className="mt-8">
        {tab === "site" && <SiteSettings value={settings.site} onSave={(p) => saveSection("site", p)} />}
        {tab === "email" && <EmailSettings value={settings.email} onSave={(p) => saveSection("email", p)} showToast={showToast} setErr={setErr} />}
        {tab === "payment" && <PaymentSettings value={settings.payment} plans={plans} reloadPlans={load} onSave={(p) => saveSection("payment", p)} showToast={showToast} setErr={setErr} />}
        {tab === "profile" && (
          <ProfileSpecsSettings
            specs={settings.profile_specs}
            moderation={settings.moderation}
            weights={settings.matching_weights}
            onSaveSpecs={(p) => saveSection("profile_specs", p)}
            onSaveModeration={(p) => saveSection("moderation", p)}
            onSaveWeights={(p) => saveSection("matching_weights", p)}
          />
        )}
        {tab === "cms" && (
          <LandingCmsSettings value={settings.landing_cms || {}} onSave={(p) => saveSection("landing_cms", p)} />
        )}
      </div>
    </div>
  );
}

// --------------------------------------------------------
// SITE
// --------------------------------------------------------
function SiteSettings({ value, onSave }) {
  const [form, setForm] = useState(value);
  useEffect(() => { setForm(value); }, [value]);
  const set = (k, v) => setForm({ ...form, [k]: v });
  const setSocial = (k, v) => setForm({ ...form, social_links: { ...form.social_links, [k]: v } });

  return (
    <SectionCard title="Site & Branding" description="Basis-identitet der vises overalt på platformen.">
      <Grid cols={2}>
        <Field label="Site-navn">
          <input className="field" value={form.name || ""} onChange={(e) => set("name", e.target.value)} data-testid="site-name-input" />
        </Field>
        <Field label="Tagline">
          <input className="field" value={form.tagline || ""} onChange={(e) => set("tagline", e.target.value)} data-testid="site-tagline-input" />
        </Field>
        <Field label="Kontakt-email">
          <input type="email" className="field" value={form.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)} data-testid="site-contact-email" />
        </Field>
        <Field label="Support-email">
          <input type="email" className="field" value={form.support_email || ""} onChange={(e) => set("support_email", e.target.value)} data-testid="site-support-email" />
        </Field>
        <Field label="Logo URL (valgfri)">
          <input className="field" value={form.logo_url || ""} onChange={(e) => set("logo_url", e.target.value)} placeholder="https://..." data-testid="site-logo-url" />
        </Field>
        <Field label="Standardsprog">
          <select className="field" value={form.default_language || "da"} onChange={(e) => set("default_language", e.target.value)} data-testid="site-lang">
            <option value="da">Dansk</option>
            <option value="en">English</option>
          </select>
        </Field>
      </Grid>

      <h4 className="mt-6 font-serif text-lg text-olive-700">Sociale links</h4>
      <Grid cols={2}>
        {["instagram", "facebook", "twitter", "tiktok"].map((k) => (
          <Field key={k} label={k.charAt(0).toUpperCase() + k.slice(1)}>
            <input className="field" value={(form.social_links || {})[k] || ""} onChange={(e) => setSocial(k, e.target.value)} data-testid={`site-social-${k}`} />
          </Field>
        ))}
      </Grid>

      <div className="mt-6 flex items-center justify-between gap-4 p-4 rounded-xl bg-sand-100 border border-sand-300">
        <div className="text-sm text-brown-600">
          <div className="font-serif text-olive-700 text-base">Vedligeholdelsestilstand</div>
          Når slået til, vises kun en simpel besked til ikke-admins.
        </div>
        <Toggle checked={!!form.maintenance_mode} onChange={(v) => set("maintenance_mode", v)} testid="site-maintenance-toggle" />
      </div>

      <SaveBar onSave={() => onSave(form)} />
    </SectionCard>
  );
}

// --------------------------------------------------------
// EMAIL
// --------------------------------------------------------
function EmailSettings({ value, onSave, showToast, setErr }) {
  const [form, setForm] = useState(value);
  const [showPwd, setShowPwd] = useState(false);
  const [newPwd, setNewPwd] = useState(""); // password is only sent if user types a new one
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);
  useEffect(() => { setForm(value); setNewPwd(""); }, [value]);

  const set = (k, v) => setForm({ ...form, [k]: v });
  const setTpl = (key, field, v) => setForm({
    ...form,
    templates: { ...form.templates, [key]: { ...form.templates[key], [field]: v } },
  });

  const save = () => {
    const payload = { ...form };
    // only send password if user typed a new one; empty = keep existing
    if (newPwd) payload.smtp_password = newPwd;
    delete payload.has_smtp_password;
    onSave(payload);
  };

  const sendTest = async () => {
    if (!testTo) return;
    setTesting(true);
    try {
      const { data } = await api.post("/admin/settings/email/test", { to_email: testTo });
      if (data.sent) showToast("Test-email sendt ✓");
      else if (data.mocked) showToast("SMTP ikke aktiveret — email logget (MOCKED)");
      else showToast(`Fejl: ${data.error || "ukendt"}`);
    } catch (e) { setErr(formatApiError(e)); }
    setTesting(false);
  };

  const applyPreset = (preset) => {
    const p = PROVIDER_PRESETS[preset];
    if (!p) return;
    setForm({
      ...form,
      smtp_host: p.host,
      smtp_port: p.port,
      smtp_use_tls: p.use_tls,
      smtp_use_ssl: p.use_ssl,
      smtp_username: p.username || form.smtp_username || "",
    });
  };

  return (
    <SectionCard title="Email-opsætning (SMTP)" description="Når SMTP er udfyldt og 'Aktiveret' er slået til, sender systemet rigtige emails.">
      <div className="mb-4 flex items-center justify-between gap-4 p-4 rounded-xl bg-sand-100 border border-sand-300">
        <div className="text-sm text-brown-600">
          <div className="font-serif text-olive-700 text-base">Aktiveret</div>
          Mens dette er slået fra, logges alle emails (MOCKED) i stedet for at blive sendt.
        </div>
        <Toggle checked={!!form.enabled} onChange={(v) => set("enabled", v)} testid="email-enabled-toggle" />
      </div>

      <div className="mb-4 rounded-xl border border-olive-500/20 bg-olive-50 p-4" data-testid="email-provider-presets">
        <div className="text-sm font-medium text-olive-700 mb-2">Hurtig-opsætning — vælg udbyder</div>
        <div className="text-xs text-brown-500 mb-3">
          Klik for at auto-udfylde SMTP-værter. Du skal stadig indsætte din <strong>API-nøgle som Password</strong> nedenfor.
        </div>
        <div className="flex flex-wrap gap-2">
          {Object.entries(PROVIDER_PRESETS).map(([key, p]) => (
            <button
              key={key}
              type="button"
              onClick={() => applyPreset(key)}
              className="inline-flex flex-col items-start gap-0.5 rounded-lg border border-sand-300 bg-white hover:border-olive-500 hover:bg-olive-100/40 px-3 py-2 text-left text-xs transition"
              data-testid={`email-preset-${key}`}>
              <span className="font-medium text-brown-700">{p.label}</span>
              <span className="text-[10px] text-brown-500">{p.hint}</span>
            </button>
          ))}
        </div>
      </div>

      <Grid cols={2}>
        <Field label="SMTP host">
          <input className="field" placeholder="smtp.gmail.com" value={form.smtp_host || ""} onChange={(e) => set("smtp_host", e.target.value)} data-testid="smtp-host" />
        </Field>
        <Field label="Port">
          <input type="number" className="field" value={form.smtp_port || 587} onChange={(e) => set("smtp_port", parseInt(e.target.value || "587", 10))} data-testid="smtp-port" />
        </Field>
        <Field label="Brugernavn">
          <input className="field" value={form.smtp_username || ""} onChange={(e) => set("smtp_username", e.target.value)} data-testid="smtp-username" />
        </Field>
        <Field label={form.has_smtp_password ? "Password (gemt — skriv nyt for at erstatte)" : "Password"}>
          <div className="relative">
            <input
              type={showPwd ? "text" : "password"}
              className="field pr-10"
              placeholder={form.has_smtp_password ? "••••••••" : ""}
              value={newPwd}
              onChange={(e) => setNewPwd(e.target.value)}
              data-testid="smtp-password"
            />
            <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-3 top-1/2 -translate-y-1/2 text-brown-500" data-testid="toggle-show-pwd">
              {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </Field>
        <Field label="Afsender-navn">
          <input className="field" value={form.from_name || ""} onChange={(e) => set("from_name", e.target.value)} data-testid="smtp-from-name" />
        </Field>
        <Field label="Afsender-email">
          <input type="email" className="field" value={form.from_email || ""} onChange={(e) => set("from_email", e.target.value)} data-testid="smtp-from-email" />
        </Field>
      </Grid>
      <div className="mt-4 flex flex-wrap gap-6">
        <label className="inline-flex items-center gap-2 text-sm text-brown-600" data-testid="smtp-tls-label">
          <input type="checkbox" checked={!!form.smtp_use_tls} onChange={(e) => set("smtp_use_tls", e.target.checked)} /> STARTTLS
        </label>
        <label className="inline-flex items-center gap-2 text-sm text-brown-600" data-testid="smtp-ssl-label">
          <input type="checkbox" checked={!!form.smtp_use_ssl} onChange={(e) => set("smtp_use_ssl", e.target.checked)} /> SSL (implicit)
        </label>
      </div>

      <h4 className="mt-8 font-serif text-lg text-olive-700">Email-skabeloner</h4>
      <p className="text-xs text-brown-500 mt-1">
        Pladsholdere du kan bruge: <code>&#123;&#123;name&#125;&#125;</code>, <code>&#123;&#123;site_name&#125;&#125;</code>, <code>&#123;&#123;site_url&#125;&#125;</code>, <code>&#123;&#123;reason&#125;&#125;</code> (ved afvist), <code>&#123;&#123;reset_url&#125;&#125;</code> (ved password-reset).
      </p>
      <div className="mt-4 space-y-4">
        {Object.entries(form.templates || {}).map(([key, tpl]) => (
          <details key={key} className="card-soft p-4">
            <summary className="cursor-pointer font-serif text-olive-700 capitalize">{key.replaceAll("_", " ")}</summary>
            <div className="mt-3 space-y-2">
              <input className="field" value={tpl.subject || ""} onChange={(e) => setTpl(key, "subject", e.target.value)} placeholder="Emne" data-testid={`tpl-${key}-subject`} />
              <textarea className="field min-h-[140px] font-mono text-sm" value={tpl.body || ""} onChange={(e) => setTpl(key, "body", e.target.value)} data-testid={`tpl-${key}-body`} />
            </div>
          </details>
        ))}
      </div>

      <div className="mt-6 flex flex-col sm:flex-row gap-3 items-start sm:items-center p-4 rounded-xl bg-gold-100/50 border border-gold-500/30">
        <div className="flex-1">
          <div className="font-serif text-olive-700">Send test-email</div>
          <p className="text-xs text-brown-500">Gem først hvis du har ændret SMTP. Uden 'Aktiveret' vil den logges i stedet.</p>
        </div>
        <input type="email" className="field max-w-xs" placeholder="mig@example.com" value={testTo} onChange={(e) => setTestTo(e.target.value)} data-testid="test-email-to" />
        <button onClick={sendTest} disabled={testing || !testTo} className="btn-primary py-2 px-4 text-sm disabled:opacity-60" data-testid="send-test-email">
          <Send className="h-4 w-4" /> {testing ? "Sender…" : "Send test"}
        </button>
      </div>

      <SaveBar onSave={save} />
    </SectionCard>
  );
}

// --------------------------------------------------------
// PAYMENT
// --------------------------------------------------------
function PaymentSettings({ value, plans, reloadPlans, onSave, showToast, setErr }) {
  const [form, setForm] = useState(value);
  const [showSecret, setShowSecret] = useState(false);
  const [showHook, setShowHook] = useState(false);
  const [newSecret, setNewSecret] = useState("");
  const [newHook, setNewHook] = useState("");
  useEffect(() => { setForm(value); setNewSecret(""); setNewHook(""); }, [value]);

  const set = (k, v) => setForm({ ...form, [k]: v });

  const save = () => {
    const payload = { ...form };
    if (newSecret) payload.stripe_secret_key = newSecret;
    if (newHook) payload.stripe_webhook_secret = newHook;
    delete payload.has_stripe_secret_key;
    delete payload.has_stripe_webhook_secret;
    onSave(payload);
  };

  return (
    <div className="space-y-6">
      <SectionCard title="Stripe-opsætning" description="Angiv dine Stripe-nøgler. Hemmelige værdier gemmes krypteret. Webhook-URL er: /api/webhook/stripe">
        <div className="mb-4 flex items-center justify-between gap-4 p-4 rounded-xl bg-sand-100 border border-sand-300">
          <div className="text-sm text-brown-600">
            <div className="font-serif text-olive-700 text-base">Test-tilstand</div>
            Bruges til at signalere at checkout-flowet er på test-miljø.
          </div>
          <Toggle checked={!!form.test_mode} onChange={(v) => set("test_mode", v)} testid="stripe-test-mode" />
        </div>

        <Grid cols={2}>
          <Field label="Publishable key">
            <input className="field" placeholder="pk_test_..." value={form.stripe_publishable_key || ""} onChange={(e) => set("stripe_publishable_key", e.target.value)} data-testid="stripe-publishable" />
          </Field>
          <Field label={form.has_stripe_secret_key ? "Secret key (gemt — skriv nyt for at erstatte)" : "Secret key"}>
            <div className="relative">
              <input type={showSecret ? "text" : "password"} className="field pr-10" placeholder={form.has_stripe_secret_key ? "••••••••" : "sk_test_..."} value={newSecret} onChange={(e) => setNewSecret(e.target.value)} data-testid="stripe-secret" />
              <button type="button" onClick={() => setShowSecret(!showSecret)} className="absolute right-3 top-1/2 -translate-y-1/2 text-brown-500">
                {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </Field>
          <Field label={form.has_stripe_webhook_secret ? "Webhook secret (gemt)" : "Webhook secret"}>
            <div className="relative">
              <input type={showHook ? "text" : "password"} className="field pr-10" placeholder={form.has_stripe_webhook_secret ? "••••••••" : "whsec_..."} value={newHook} onChange={(e) => setNewHook(e.target.value)} data-testid="stripe-webhook" />
              <button type="button" onClick={() => setShowHook(!showHook)} className="absolute right-3 top-1/2 -translate-y-1/2 text-brown-500">
                {showHook ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </Field>
          <Field label="Standardvaluta">
            <select className="field" value={form.currency || "usd"} onChange={(e) => set("currency", e.target.value)} data-testid="stripe-currency">
              <option value="usd">USD</option>
              <option value="eur">EUR</option>
              <option value="dkk">DKK</option>
              <option value="gbp">GBP</option>
            </select>
          </Field>
        </Grid>

        <SaveBar onSave={save} />
      </SectionCard>

      <PlansManager plans={plans} reloadPlans={reloadPlans} showToast={showToast} setErr={setErr} />
    </div>
  );
}

function PlansManager({ plans, reloadPlans, showToast, setErr }) {
  const blank = { key: "", name: "", price: 0, currency: "usd", duration_days: 30, description: "", features: [], active: true, order: (plans.length || 0) + 1, highlight: false };
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const isNew = editing === "new";
  const isEdit = editing && editing !== "new";

  const openNew = () => { setForm({ ...blank, order: (plans.length || 0) + 1 }); setEditing("new"); };
  const openEdit = (p) => { setForm({ ...p, features: p.features || [] }); setEditing(p.id); };
  const close = () => { setEditing(null); };

  const save = async () => {
    try {
      if (isNew) {
        await api.post("/admin/plans", form);
      } else if (isEdit) {
        const { id, key, created_at, updated_at, ...patch } = form;
        await api.put(`/admin/plans/${editing}`, patch);
      }
      showToast("Plan gemt ✓");
      close();
      reloadPlans();
    } catch (e) { setErr(formatApiError(e)); }
  };

  const del = async (p) => {
    if (!(await confirmDialog({ title: "Slet planen?", body: `"${p.name}" bliver fjernet permanent.`, tone: "danger", confirmText: "Slet plan" }))) return;
    try { await api.delete(`/admin/plans/${p.id}`); reloadPlans(); showToast("Plan slettet"); }
    catch (e) { setErr(formatApiError(e)); }
  };

  return (
    <SectionCard title="Planer (medlemskaber)" description="Disse planer vises på /pricing. Redigér pris, varighed, features og rækkefølge.">
      <div className="space-y-3">
        {plans.map((p) => (
          <div key={p.id} className="card-soft p-5 flex flex-wrap items-center gap-4" data-testid={`plan-row-${p.key}`}>
            <div className="flex-1 min-w-[200px]">
              <div className="flex items-center gap-2">
                <span className="font-serif text-lg text-olive-700">{p.name}</span>
                {p.highlight && <span className="text-[10px] uppercase tracking-wider bg-gold-500 text-white px-2 py-0.5 rounded-full">Anbefalet</span>}
                {!p.active && <span className="text-[10px] uppercase tracking-wider bg-sand-300 text-brown-600 px-2 py-0.5 rounded-full">inaktiv</span>}
              </div>
              <div className="text-xs text-brown-500">nøgle: <code>{p.key}</code> · {p.duration_days} dage · {(p.features || []).length} features</div>
            </div>
            <div className="font-serif text-2xl text-olive-700">{p.price} <span className="text-xs text-brown-500 uppercase">{p.currency}</span></div>
            <button className="btn-secondary py-2 px-4 text-sm" onClick={() => openEdit(p)} data-testid={`edit-plan-${p.key}`}>Redigér</button>
            <button className="inline-flex items-center gap-1 rounded-full border border-destructive/40 px-3 py-2 text-destructive hover:bg-destructive/10 text-sm" onClick={() => del(p)} data-testid={`delete-plan-${p.key}`}>
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
      <div className="mt-4">
        <button className="btn-primary" onClick={openNew} data-testid="add-plan-btn"><Plus className="h-4 w-4" /> Tilføj plan</button>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={close}>
          <div className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="plan-editor-modal">
            <div className="flex items-center justify-between">
              <h3 className="font-serif text-2xl text-olive-700">{isNew ? "Ny plan" : "Redigér plan"}</h3>
              <button onClick={close} className="text-brown-500 text-2xl leading-none">&times;</button>
            </div>
            <div className="mt-4 space-y-4">
              <Grid cols={2}>
                <Field label="Nøgle (stabil, bruges internt)">
                  <input className="field" value={form.key || ""} disabled={!isNew} onChange={(e) => setForm({ ...form, key: e.target.value.toLowerCase() })} placeholder="monthly" data-testid="plan-key" />
                </Field>
                <Field label="Navn (vises på pricing)">
                  <input className="field" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="plan-name" />
                </Field>
                <Field label="Pris">
                  <input type="number" step="0.01" className="field" value={form.price} onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value || "0") })} data-testid="plan-price" />
                </Field>
                <Field label="Valuta">
                  <select className="field" value={form.currency || "usd"} onChange={(e) => setForm({ ...form, currency: e.target.value })} data-testid="plan-currency">
                    <option value="usd">USD</option><option value="eur">EUR</option><option value="dkk">DKK</option><option value="gbp">GBP</option>
                  </select>
                </Field>
                <Field label="Varighed (dage)">
                  <input type="number" className="field" value={form.duration_days} onChange={(e) => setForm({ ...form, duration_days: parseInt(e.target.value || "30", 10) })} data-testid="plan-duration" />
                </Field>
                <Field label="Rækkefølge">
                  <input type="number" className="field" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value || "0", 10) })} data-testid="plan-order" />
                </Field>
              </Grid>
              <Field label="Beskrivelse">
                <textarea className="field" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="plan-description" />
              </Field>
              <Field label="Features (én pr. linje)">
                <textarea className="field min-h-[120px]" value={(form.features || []).join("\n")} onChange={(e) => setForm({ ...form, features: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })} data-testid="plan-features" />
              </Field>
              <Field label="Stripe price_id (valgfri)">
                <input className="field" value={form.stripe_price_id || ""} onChange={(e) => setForm({ ...form, stripe_price_id: e.target.value })} placeholder="price_..." data-testid="plan-stripe-id" />
              </Field>
              <div className="flex flex-wrap items-center gap-6">
                <label className="inline-flex items-center gap-2 text-sm text-brown-600">
                  <input type="checkbox" checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="plan-active" /> Aktiv
                </label>
                <label className="inline-flex items-center gap-2 text-sm text-brown-600">
                  <input type="checkbox" checked={!!form.highlight} onChange={(e) => setForm({ ...form, highlight: e.target.checked })} data-testid="plan-highlight" /> Fremhæv (Anbefalet)
                </label>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={close} className="btn-secondary">Annullér</button>
              <button onClick={save} className="btn-primary" data-testid="plan-save"><Save className="h-4 w-4" /> Gem</button>
            </div>
          </div>
        </div>
      )}
    </SectionCard>
  );
}

// --------------------------------------------------------
// PROFILE SPECS / MODERATION / WEIGHTS
// --------------------------------------------------------
function ProfileSpecsSettings({ specs, moderation, weights, onSaveSpecs, onSaveModeration, onSaveWeights }) {
  const [s, setS] = useState(specs);
  const [m, setM] = useState(moderation);
  const [w, setW] = useState(weights);
  useEffect(() => { setS(specs); }, [specs]);
  useEffect(() => { setM(moderation); }, [moderation]);
  useEffect(() => { setW(weights); }, [weights]);

  return (
    <div className="space-y-6">
      <SectionCard title="Profil-dropdowns" description="Alle valgmuligheder i profil-wizarden. Brug kommaer eller linjeskift.">
        <Grid cols={2}>
          <TextListField label="Uddannelsesniveauer" value={s.education_levels} onChange={(v) => setS({ ...s, education_levels: v })} testid="spec-education" />
          <TextListField label="Lande" value={s.countries} onChange={(v) => setS({ ...s, countries: v })} testid="spec-countries" />
          <TextListField label="Byer" value={s.cities} onChange={(v) => setS({ ...s, cities: v })} testid="spec-cities" />
          <TextListField label="Sprog" value={s.languages} onChange={(v) => setS({ ...s, languages: v })} testid="spec-languages" />
          <TextListField label="Værdier (multiselect)" value={s.values} onChange={(v) => setS({ ...s, values: v })} testid="spec-values" />
          <TextListField label="Hobbyer" value={s.hobbies} onChange={(v) => setS({ ...s, hobbies: v })} testid="spec-hobbies" />
        </Grid>

        <h4 className="mt-6 font-serif text-lg text-olive-700">Sekter</h4>
        <OptionPairEditor options={s.sects} onChange={(v) => setS({ ...s, sects: v })} testid="spec-sects" />

        <h4 className="mt-6 font-serif text-lg text-olive-700">Religiøs praksis</h4>
        <OptionPairEditor options={s.religious_practices} onChange={(v) => setS({ ...s, religious_practices: v })} testid="spec-practices" />

        <h4 className="mt-6 font-serif text-lg text-olive-700">Civilstand</h4>
        <OptionPairEditor options={s.marital_statuses} onChange={(v) => setS({ ...s, marital_statuses: v })} testid="spec-marital" />

        <SaveBar onSave={() => onSaveSpecs(s)} />
      </SectionCard>

      <SectionCard title="Moderation" description="Brugerdefineret ordliste der tilføjes til den indbyggede profanity-filter.">
        <TextListField label="Ekstra filter-ord (komma- eller linje-separeret)" value={m.custom_profanity_words} onChange={(v) => setM({ ...m, custom_profanity_words: v })} testid="moderation-words" />
        <label className="inline-flex items-center gap-2 text-sm text-brown-600 mt-4">
          <input type="checkbox" checked={!!m.require_photo_for_approval} onChange={(e) => setM({ ...m, require_photo_for_approval: e.target.checked })} data-testid="require-photo" />
          Kræv mindst ét foto før godkendelse
        </label>
        <SaveBar onSave={() => onSaveModeration(m)} />
      </SectionCard>

      <SectionCard title="Matchmaking-vægte" description="Hvor meget tæller hver faktor i kompatibilitetsscoren (0-100). Summen behøver ikke være præcis 100.">
        <Grid cols={2}>
          {["faith", "age", "location", "values", "marital_status"].map((key) => (
            <Field key={key} label={key.replaceAll("_", " ")}>
              <input type="number" min="0" max="100" className="field" value={w[key] ?? 0} onChange={(e) => setW({ ...w, [key]: parseInt(e.target.value || "0", 10) })} data-testid={`weight-${key}`} />
            </Field>
          ))}
        </Grid>
        <div className="mt-3 text-xs text-brown-500">Sum: {Object.values(w).reduce((a, b) => a + (b || 0), 0)}</div>
        <SaveBar onSave={() => onSaveWeights(w)} />
      </SectionCard>
    </div>
  );
}

// --------------------------------------------------------
// LANDING CMS
// --------------------------------------------------------
function LandingCmsSettings({ value, onSave }) {
  const [form, setForm] = useState(value);
  useEffect(() => { setForm(value); }, [value]);
  const set = (k, v) => setForm({ ...form, [k]: v });

  const sections = [
    { title: "Hero (øverst)", keys: [
      ["hero_eyebrow", "Eyebrow (lille tekst over overskrift)"],
      ["hero_title_html", "Titel (HTML tilladt — brug <em> for farvet fremhævning)", true],
      ["hero_description", "Beskrivelse", true],
      ["hero_cta_primary", "Primær knap-tekst"],
      ["hero_cta_secondary", "Sekundær knap-tekst"],
    ]},
    { title: "Værdier", keys: [
      ["values_eyebrow", "Eyebrow"],
      ["values_title", "Titel"],
      ["values_description", "Beskrivelse", true],
    ]},
    { title: "Sådan virker det", keys: [
      ["how_eyebrow", "Eyebrow"],
      ["how_title", "Titel"],
    ]},
    { title: "Qur'an-vers (cinematic band)", keys: [
      ["quran_verse", "Vers", true],
      ["quran_reference", "Kildehenvisning"],
    ]},
    { title: "Udtalelser", keys: [
      ["testimonials_eyebrow", "Eyebrow"],
      ["testimonials_title", "Titel"],
    ]},
    { title: "Afsluttende CTA", keys: [
      ["final_cta_title", "Titel"],
      ["final_cta_description", "Beskrivelse", true],
    ]},
  ];

  return (
    <SectionCard title="Forsidens indhold" description="Rediger alle tekster på /. Ændringer vises straks efter Gem (hard-reload for cache).">
      {sections.map((s) => (
        <div key={s.title} className="mb-6 pb-6 border-b border-sand-200 last:border-b-0">
          <h4 className="font-serif text-lg text-olive-700 mb-3">{s.title}</h4>
          <div className="space-y-3">
            {s.keys.map(([k, label, multiline]) => (
              <Field key={k} label={label}>
                {multiline ? (
                  <textarea className="field min-h-[80px]" value={form[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`cms-${k}`} />
                ) : (
                  <input className="field" value={form[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`cms-${k}`} />
                )}
              </Field>
            ))}
          </div>
        </div>
      ))}
      <SaveBar onSave={() => onSave(form)} />
    </SectionCard>
  );
}

// --------------------------------------------------------
// Small UI helpers
// --------------------------------------------------------
function SectionCard({ title, description, children }) {
  return (
    <div className="card-soft p-6 sm:p-8">
      <h3 className="font-serif text-2xl text-olive-700">{title}</h3>
      {description && <p className="mt-1 text-sm text-brown-500">{description}</p>}
      <div className="mt-5">{children}</div>
    </div>
  );
}
function Grid({ cols = 2, children }) {
  const cls = cols === 2 ? "grid gap-4 sm:grid-cols-2" : "grid gap-4 sm:grid-cols-3";
  return <div className={cls}>{children}</div>;
}
function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wider text-brown-500">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
function Toggle({ checked, onChange, testid }) {
  return (
    <button type="button" onClick={() => onChange(!checked)} className={`relative inline-flex h-7 w-12 rounded-full transition ${checked ? "bg-olive-500" : "bg-sand-300"}`} data-testid={testid}>
      <span className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition ${checked ? "left-5" : "left-0.5"}`} />
    </button>
  );
}
function SaveBar({ onSave }) {
  return (
    <div className="mt-6 pt-4 border-t border-sand-200 flex justify-end">
      <button onClick={onSave} className="btn-primary" data-testid="save-section"><Save className="h-4 w-4" /> Gem</button>
    </div>
  );
}
function TextListField({ label, value, onChange, testid }) {
  const toText = (arr) => (Array.isArray(arr) ? arr.join("\n") : "");
  const toList = (text) => text.split(/[,\n]/).map((s) => s.trim()).filter(Boolean);
  return (
    <Field label={label}>
      <textarea
        className="field min-h-[100px] font-mono text-sm"
        value={toText(value)}
        onChange={(e) => onChange(toList(e.target.value))}
        data-testid={testid}
      />
    </Field>
  );
}
function OptionPairEditor({ options, onChange, testid }) {
  const safe = Array.isArray(options) ? options : [];
  const upd = (idx, field, v) => {
    const copy = [...safe];
    copy[idx] = { ...copy[idx], [field]: v };
    onChange(copy);
  };
  const add = () => onChange([...safe, { value: "", label: "" }]);
  const remove = (idx) => onChange(safe.filter((_, i) => i !== idx));
  return (
    <div className="space-y-2" data-testid={testid}>
      {safe.map((o, idx) => (
        <div key={idx} className="flex gap-2 items-center">
          <input className="field flex-1" placeholder="værdi (intern)" value={o.value || ""} onChange={(e) => upd(idx, "value", e.target.value)} />
          <input className="field flex-1" placeholder="etiket (vises)" value={o.label || ""} onChange={(e) => upd(idx, "label", e.target.value)} />
          <button type="button" onClick={() => remove(idx)} className="text-destructive p-2"><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
      <button type="button" onClick={add} className="btn-secondary text-sm py-2 px-4"><Plus className="h-4 w-4" /> Tilføj</button>
    </div>
  );
}
